import { createContext, useContext, useMemo, type ReactNode } from "react";
import { createTranslation } from "@bfc-software/bfc-web-ui/i18n/use-translation";
import type { NameSpaceKeys } from "@bfc-software/bfc-web-ui/i18n/settings";

// Derived, not spelled `t: TFunction`: this file must pass the lint rule it exists to enable.
type Translation = ReturnType<typeof createTranslation>;
type Localization = readonly [t: Translation["t"], i18n: Translation["i18n"]];

const LocalizationContext = createContext<Localization | null>(null);

type LocalizationProviderProps = {
  lang: string;
  namespace: NameSpaceKeys;
  children: ReactNode;
};

export function LocalizationProvider({
  lang,
  namespace,
  children,
}: LocalizationProviderProps) {
  const value = useMemo<Localization>(() => {
    const { t, i18n } = createTranslation({ lang, namespace });
    return [t, i18n] as const;
  }, [lang, namespace]); // memoized: one i18next instance per (lang, namespace), not per render
  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

export function useCurrentLocalization(): Localization {
  const value = useContext(LocalizationContext);
  if (value === null) {
    throw new Error(
      "useCurrentLocalization must be used inside a <LocalizationProvider>",
    );
  }
  return value;
}
